import React from 'react';

/**
 * CandidateCardSkeleton - Loading skeleton for candidate recommendation cards
 * 
 * @param {number} delay - Animation delay in milliseconds for staggered loading effect
 * @param {string} variant - Skeleton variant: 'default', 'compact'
 */
const CandidateCardSkeleton = ({ delay = 0, variant = 'default' }) => {
  const isCompact = variant === 'compact';
  
  return (
    <div 
      className="bg-surface rounded-lg border border-border p-6 animate-pulse hover:shadow-lg transition-shadow duration-200"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header Section */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          {/* Avatar skeleton */}
          <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>
          
          {/* Candidate info skeleton */}
          <div className="flex-1 min-w-0">
            <div className="w-40 h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
            <div className="w-32 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
            <div className="flex items-center space-x-4">
              <div className="w-24 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              <div className="flex items-center space-x-1">
                <div className="w-4 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                <div className="w-8 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                <div className="w-12 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Match score skeleton */}
        <div className="text-right">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-4 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
            <div className="w-12 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          </div>
          <div className="w-20 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
        </div>
      </div>

      {/* Match score bar skeleton */}
      <div className="mb-4">
        <div className="w-full h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
      </div>

      {!isCompact && (
        <>
          {/* Grid Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Key Skills */}
            <div>
              <div className="w-20 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-16 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                ))}
                <div className="w-12 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
              </div>
            </div>
            
            {/* AI Insights */}
            <div>
              <div className="w-24 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <div className="w-28 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-10 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
                <div className="flex justify-between">
                  <div className="w-20 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="w-12 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Match Section */}
          <div className="mb-4">
            <div className="w-28 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
            <div className="space-y-1">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-start space-x-2">
                  <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mt-0.5 flex-shrink-0"></div>
                  <div className="w-48 h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Action Section */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          <div className="w-20 h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-20 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
          <div className="w-24 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
        </div>
      </div>
    </div>
  );
};

export default CandidateCardSkeleton;
